<template>
  <div class="c-modal" :class="rootClasses">
    <header class="c-modal-header">
      <slot name="header">
        <CIcon v-if="icon" :name="icon" :size="24"/>
        <h2 v-if="title">{{ title }}</h2>
      </slot>
      <CButton theme="ghost-secondary" @click="cancel" class="c-modal-header-cancel">
        <CIcon name="close"/>
      </CButton>
    </header>

    <header v-if="$slots.toolbar" class="c-modal-toolbar">
      <slot name="toolbar"/>
    </header>

    <main v-if="$slots.body" :class="['c-modal-body', !hasPadding ? 'c-modal-body-no-padding' : '', hideFooter ? 'c-modal-body-border-radius' : '']">
      <slot name="body"/>
    </main>

    <footer v-if="!hideFooter" class="c-modal-footer">
      <slot name="footer">
        <CButton @click="cancel" class="c-modal-cancel">{{ finalCancelLabel }}</CButton>
        <CButton @click="confirm" :type="confirmType" theme="main-primary">{{ finalConfirmLabel }}</CButton>
      </slot>
    </footer>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import CIcon from '@/shared/components/CIcon.vue';
  import CButton from '@/shared/components/buttons/CButton.vue';
  import { BasicModalResult, MODAL_CANCELLED, MODAL_CONFIRMED } from '../utils/Modal';
  import { hasForm, useForm } from '@/shared/utils/component';

  const i18n = useI18n();

  const props = withDefaults(defineProps<{
    title?: string,
    icon?: string,
    cancelLabel?: string,
    confirmLabel?: string,
    fullHeight?: boolean,
    large?: boolean,
    position?: 'left' | 'center' | 'right',
    hasPadding?: boolean,
    hideFooter?: boolean,
  }>(), {
    position: 'center',
    hasPadding: true,
  });

  const emit = defineEmits<{
    (event: 'close', result: BasicModalResult): void,
  }>();

  const rootClasses = computed(() => ({
    'c-modal-full-height': props.fullHeight || props.position === 'left' || props.position === 'right',
    'c-modal-large': props.large,
    'c-modal-left': props.position === 'left',
    'c-modal-right': props.position === 'right',
  }));
  const finalCancelLabel = computed(() => props.cancelLabel || i18n.t('general.cancel'));
  const finalConfirmLabel = computed(() => props.confirmLabel || i18n.t('general.confirm'));

  const form = useForm();

  const formPresent = computed(() => hasForm() || !!form);

  const confirmType = formPresent.value ? 'submit' : 'button';

  function confirm() {
    // if we are in a form, clicking the submit button will trigger a form submit, which should be handled separately
    if (!(formPresent.value)) emit('close', MODAL_CONFIRMED);
  }

  function cancel() {
    form?.close();
    emit('close', MODAL_CANCELLED);
  }
</script>

<style lang="scss">
  .c-modal {
    border-radius: $border-radius*2;
    display: flex;
    flex-direction: column;
    color: $modal-fg;
    background-color: $modal-bg;
    max-height: calc(100vh - 10rem); // accounts for container margins

    &-full-height {
      height: 100vh; // NOTE this is constrained by above max-height
    }

    > * {
      padding: 1rem;
    }

    &-header {
      display: flex;
      align-items: center;
      gap: .5rem;
      color: $title-fg;

      &-cancel {
        margin: -.5rem;
        margin-left: auto;
      }
    }

    &-body {
      flex: 1;
      overflow: auto;
      border-top: 1px solid $neutral-200;
      border-bottom: 1px solid $neutral-200;
    }

    &-footer {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: .5rem;
    }

    &-cancel {
      margin-right: auto;
    }

    &-right, &-left {
      border-radius: 0;
      max-height: unset;
    }
  }

  .c-modal > .c-modal-body.c-modal-body-no-padding {
    padding: 0;
  }

  .c-modal-body-border-radius {
    border-radius: 0 0 $border-radius*2 $border-radius*2;
  }
</style>